import React from 'react';
import PropTypes from 'prop-types';
import { forbidExtraProps } from 'airbnb-prop-types';
import cx from 'classnames';

import { DateRangePickerInputPhrases } from './utils/defaultPhrases';
import getPhrasePropTypes from './utils/getPhrasePropTypes';
import openDirectionShape from './shapes/OpenDirectionShape';

import DateInput from './DateInput';
import IconPositionShape from './shapes/IconPositionShape';

import {
    IconArrowBack as LeftArrow,
    IconArrowForward as RightArrow,
    IconClose as CloseButton,
    IconCalendar as CalendarIcon
} from '../icons/Icons';

import { START_DATE, END_DATE, ICON_BEFORE_POSITION, ICON_AFTER_POSITION, OPEN_DOWN } from './constants';

const propTypes = forbidExtraProps({
    startDateId: PropTypes.string,
    startDatePlaceholderText: PropTypes.string,
    startDateInputLabel: PropTypes.string,
    // screenReaderMessage: PropTypes.string,

    endDateId: PropTypes.string,
    endDatePlaceholderText: PropTypes.string,
    endDateInputLabel: PropTypes.string,

    onStartDateFocus: PropTypes.func,
    onEndDateFocus: PropTypes.func,
    // onStartDateChange: PropTypes.func,
    onStartDateBlur: PropTypes.func,
    // onEndDateChange: PropTypes.func,
    onEndDateBlur: PropTypes.func,
    onStartDateShiftTab: PropTypes.func,
    onEndDateTab: PropTypes.func,
    onClearDates: PropTypes.func,
    onArrowDown: PropTypes.func,
    onQuestionMark: PropTypes.func,

    startDate: PropTypes.string,
    startDateError: PropTypes.string,
    endDate: PropTypes.string,
    endDateError: PropTypes.string,

    isStartDateFocused: PropTypes.bool,
    isEndDateFocused: PropTypes.bool,
    showClearDates: PropTypes.bool,
    disabled: PropTypes.bool,
    required: PropTypes.bool,
    readOnly: PropTypes.bool,
    openDirection: openDirectionShape,
    showCaret: PropTypes.bool,
    showDefaultInputIcon: PropTypes.bool,
    inputIconPosition: IconPositionShape,
    customInputIcon: PropTypes.node,
    customArrowIcon: PropTypes.node,
    customCloseIcon: PropTypes.node,

    // accessibility
    isFocused: PropTypes.bool, // describes actual DOM focus

    // i18n
    phrases: PropTypes.shape(getPhrasePropTypes(DateRangePickerInputPhrases)),

    isRTL: PropTypes.bool
});

const defaultProps = {
    startDateId: START_DATE,
    endDateId: END_DATE,
    startDatePlaceholderText: 'Check in',
    endDatePlaceholderText: 'Check out',
    // screenReaderMessage: '',
    onStartDateFocus() {},
    onEndDateFocus() {},
    // onStartDateChange() {},
    // onEndDateChange() {},
    onStartDateShiftTab() {},
    onEndDateTab() {},
    onClearDates() {},
    onArrowDown() {},
    onQuestionMark() {},

    startDate: '',
    startDateError: null,
    endDate: '',
    endDateError: null,

    isStartDateFocused: false,
    isEndDateFocused: false,
    showClearDates: false,
    disabled: false,
    required: false,
    readOnly: false,
    openDirection: OPEN_DOWN,
    showCaret: false,
    showDefaultInputIcon: false,
    inputIconPosition: ICON_BEFORE_POSITION,
    customInputIcon: null,
    customArrowIcon: null,
    customCloseIcon: null,

    // accessibility
    isFocused: false,

    // i18n
    phrases: DateRangePickerInputPhrases,

    isRTL: false
};

export default class DateRangePickerInput extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            isClearDatesHovered: false
        };

        this.onClearDatesMouseEnter = this.onClearDatesMouseEnter.bind(this);
        this.onClearDatesMouseLeave = this.onClearDatesMouseLeave.bind(this);
    }

    onClearDatesMouseEnter() {
        this.setState({
            isClearDatesHovered: true
        });
    }

    onClearDatesMouseLeave() {
        this.setState({
            isClearDatesHovered: false
        });
    }

    render() {
        const { isClearDatesHovered } = this.state;
        const {
            startDate,
            startDateId,
            startDatePlaceholderText,
            startDateInputLabel,
            startDateError,
            // screenReaderMessage,
            isStartDateFocused,
            // onStartDateChange,
            onStartDateBlur,
            onStartDateFocus,
            onStartDateShiftTab,
            endDate,
            endDateId,
            endDatePlaceholderText,
            endDateInputLabel,
            endDateError,
            isEndDateFocused,
            // onEndDateChange,
            onEndDateBlur,
            onEndDateFocus,
            onEndDateTab,
            onArrowDown,
            onQuestionMark,
            onClearDates,
            showClearDates,
            disabled,
            required,
            readOnly,
            openDirection,
            showCaret,
            showDefaultInputIcon,
            inputIconPosition,
            customInputIcon,
            customArrowIcon,
            customCloseIcon,
            isFocused,
            phrases,
            isRTL
        } = this.props;

        const calendarIcon = customInputIcon || <CalendarIcon />;
        const arrowIcon = customArrowIcon || (isRTL ? <LeftArrow /> : <RightArrow />);
        const closeIcon = customCloseIcon || <CloseButton />;
        // const screenReaderText = screenReaderMessage || phrases.keyboardNavigationInstructions;
        const inputIcon = (showDefaultInputIcon || customInputIcon !== null) && (
            <button type="button" className="DateRangePickerInput__calendar-icon calendar-icon" disabled={disabled} aria-label={phrases.focusStartDate} onClick={onArrowDown}>
                {calendarIcon}
            </button>
        );

        return (
            <div>
                <div
                    className={cx('DateRangePickerInput', 'sarsa-date-picker', {
                        'DateRangePickerInput--disabled': disabled,
                        'DateRangePickerInput--rtl': isRTL
                    })}>
                    {inputIconPosition === ICON_BEFORE_POSITION && inputIcon}

                    <DateInput
                        id={startDateId}
                        label={startDateInputLabel || startDatePlaceholderText}
                        placeholder={startDatePlaceholderText}
                        inputValue={startDate}
                        hasError={startDateError.length > 0}
                        errorMessage={startDateError}
                        // screenReaderMessage={screenReaderText}
                        focused={isStartDateFocused}
                        isFocused={isFocused}
                        disabled={disabled}
                        required={required}
                        readOnly={readOnly}
                        openDirection={openDirection}
                        showCaret={showCaret}
                        // onChange={onStartDateChange}
                        onBlur={onStartDateBlur}
                        onFocus={onStartDateFocus}
                        onKeyDownShiftTab={onStartDateShiftTab}
                        onKeyDownArrowDown={onArrowDown}
                        onKeyDownQuestionMark={onQuestionMark} />

                    <div className="DateRangePickerInput__arrow" aria-hidden="true" role="presentation">
                        {arrowIcon}
                    </div>

                    <DateInput
                        id={endDateId}
                        label={endDateInputLabel || endDatePlaceholderText}
                        placeholder={endDatePlaceholderText}
                        inputValue={endDate}
                        hasError={endDateError.length > 0}
                        errorMessage={endDateError}
                        // screenReaderMessage={screenReaderText}
                        focused={isEndDateFocused}
                        isFocused={isFocused}
                        disabled={disabled}
                        required={required}
                        readOnly={readOnly}
                        openDirection={openDirection}
                        showCaret={showCaret}
                        // onChange={onEndDateChange}
                        onBlur={onEndDateBlur}
                        onFocus={onEndDateFocus}
                        onKeyDownTab={onEndDateTab}
                        onKeyDownArrowDown={onArrowDown}
                        onKeyDownQuestionMark={onQuestionMark} />

                    {showClearDates && (
                        <button
                            type="button"
                            aria-label={phrases.clearDates}
                            className={cx('DateRangePickerInput__clear-dates', 'sarsa-date-picker-clear-btn', {
                                'DateRangePickerInput__clear-dates--hide': !(startDate || startDateError) && !(endDate || endDateError),
                                'DateRangePickerInput__clear-dates--hover': isClearDatesHovered
                            })}
                            disabled={disabled}
                            onMouseEnter={this.onClearDatesMouseEnter}
                            onMouseLeave={this.onClearDatesMouseLeave}
                            onClick={onClearDates}>
                            <div className="DateRangePickerInput__close-icon">{closeIcon}</div>
                        </button>
                    )}

                    {inputIconPosition === ICON_AFTER_POSITION && inputIcon}

                </div>
                <div className="sarsa-date-picker-error" role="alert" aria-live="assertive">
                    {startDateError && (
                        <div>Check in {startDateError}</div>
                    )}
                    {endDateError && (
                        <div>Check out {endDateError}</div>
                    )}
                </div>
            </div>
        );
    }
}

DateRangePickerInput.propTypes = propTypes;
DateRangePickerInput.defaultProps = defaultProps;
